import { FilterType, IFilterItem } from "@kuika/kuika-cl-model";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { ColorPickerPositionHelper } from "../../../../shared/utility/color-picker-position-helper";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { ColorPicker } from "./styling-color-picker";
import { defaultFilterItem, getFilterTypeGroups } from "./styling-effects-helpers";

export interface FilterPopoverContentProps {
  item: IFilterItem;
  onPatch: (patch: Partial<IFilterItem>) => void;
  onTypeChange: (next: IFilterItem) => void;
  /** Filters section allows drop-shadow; backdrop-filter section does not. */
  allowDropShadow?: boolean;
  dataTestIdPrefix?: string;
}

interface SliderConfig {
  min: number;
  max: number;
  step: number;
  precision: number;
  unit: string;
  defaultValue: number;
  label: string;
}

// drop-shadow is NOT a single-slider filter — it is rendered via a dedicated
// branch with X / Y / Blur sliders + a color picker, so it is excluded here.
const SLIDER_CONFIG_BY_TYPE: Record<Exclude<FilterType, "drop-shadow">, SliderConfig> = {
  blur: { min: 0, max: 50, step: 1, precision: 0, unit: "px", defaultValue: 4, label: "Blur" },
  brightness: { min: 0, max: 3, step: 0.05, precision: 2, unit: "", defaultValue: 1, label: "Brightness" },
  contrast: { min: 0, max: 3, step: 0.05, precision: 2, unit: "", defaultValue: 1, label: "Contrast" },
  saturate: { min: 0, max: 3, step: 0.05, precision: 2, unit: "", defaultValue: 1, label: "Saturation" },
  grayscale: { min: 0, max: 100, step: 1, precision: 0, unit: "%", defaultValue: 100, label: "Grayscale" },
  invert: { min: 0, max: 100, step: 1, precision: 0, unit: "%", defaultValue: 100, label: "Invert" },
  sepia: { min: 0, max: 100, step: 1, precision: 0, unit: "%", defaultValue: 100, label: "Sepia" },
  "hue-rotate": { min: 0, max: 360, step: 1, precision: 0, unit: "deg", defaultValue: 0, label: "Hue Rotate" }
};

// drop-shadow offset sliders allow negative values (directional shadows);
// blur is clamped to >= 0 (CSS forbids negative blur radius).
const DROP_SHADOW_OFFSET_MIN: number = -50;
const DROP_SHADOW_OFFSET_MAX: number = 50;
const DROP_SHADOW_BLUR_MAX: number = 100;
const DROP_SHADOW_DEFAULT_COLOR: string = "rgba(0,0,0,0.35)";

const FilterPopoverContent: React.FC<FilterPopoverContentProps> = ({
  item,
  onPatch,
  onTypeChange,
  allowDropShadow = false,
  dataTestIdPrefix
}) => {
  const [isColorPickerActive, setIsColorPickerActive] = useState<boolean>(false);
  const testIdBase: string = dataTestIdPrefix ?? "effects-filter";

  const typeSelector: React.ReactNode = (
    <div className="effects-filter__row">
      <KuikaSmallText style={{ color: "var(--dark-2)" }}>Type</KuikaSmallText>
      <KuikaSelectbox
        size="sm"
        groups={getFilterTypeGroups(allowDropShadow)}
        value={item.type}
        onChange={(next: string | number | (string | number)[] | null | any): void => {
          if (typeof next === "string") {
            // Type change rebuilds the item from defaults so the previous
            // value/unit (or drop-shadow offsets) cannot leak across
            // incompatible scales (px → % → shadow offsets).
            onTypeChange({ ...defaultFilterItem(next as FilterType), id: item.id });
          }
        }}
        dataTestId={`${testIdBase}-type`}
      />
    </div>
  );

  if (item.type === "drop-shadow") {
    const color: string = item.color ?? DROP_SHADOW_DEFAULT_COLOR;
    const toggleColorPicker = (event?: React.MouseEvent<HTMLElement>): void => {
      setIsColorPickerActive((prev: boolean): boolean => !prev);
      if (event) {
        if (typeof event.persist === "function") {
          event.persist();
        }
        const nativeEvent: MouseEvent = event.nativeEvent;
        ColorPickerPositionHelper.saveLastClickEvent(nativeEvent);
        KMainFunctions.setColorTriggeredPickerPosition(nativeEvent);
      }
    };

    return (
      <div className="effects-filter__popover">
        {typeSelector}
        <div className="effects-filter__row">
          <KuikaSmallText style={{ color: "var(--dark-2)" }}>X</KuikaSmallText>
          <KuikaSlider
            value={item.offsetX ?? 0}
            onChange={(next: number): void => onPatch({ offsetX: next })}
            min={DROP_SHADOW_OFFSET_MIN}
            max={DROP_SHADOW_OFFSET_MAX}
            step={1}
            precision={0}
            unit="px"
            size="sm"
            dataTestId={`${testIdBase}-offset-x`}
          />
        </div>
        <div className="effects-filter__row">
          <KuikaSmallText style={{ color: "var(--dark-2)" }}>Y</KuikaSmallText>
          <KuikaSlider
            value={item.offsetY ?? 0}
            onChange={(next: number): void => onPatch({ offsetY: next })}
            min={DROP_SHADOW_OFFSET_MIN}
            max={DROP_SHADOW_OFFSET_MAX}
            step={1}
            precision={0}
            unit="px"
            size="sm"
            dataTestId={`${testIdBase}-offset-y`}
          />
        </div>
        <div className="effects-filter__row">
          <KuikaSmallText style={{ color: "var(--dark-2)" }}>Blur</KuikaSmallText>
          <KuikaSlider
            value={item.blur ?? 0}
            onChange={(next: number): void => onPatch({ blur: next })}
            min={0}
            max={DROP_SHADOW_BLUR_MAX}
            step={1}
            precision={0}
            unit="px"
            size="sm"
            dataTestId={`${testIdBase}-blur`}
          />
        </div>
        <div className="effects-filter__row">
          <KuikaSmallText style={{ color: "var(--dark-2)" }}>Color</KuikaSmallText>
          <div
            className="kuika-styling-panel__color-input-wrapper kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--full"
            style={{ gridTemplateColumns: "none" }}
          >
            <KuikaInput
              readOnly
              size="sm"
              style={{ width: "100%" }}
              value={KMainFunctions.convertRgbaToHex(color)}
              className="kuika-styling-panel__color-input"
              onClick={(e: React.MouseEvent<HTMLElement>): void => toggleColorPicker(e)}
              dataTestId={`${testIdBase}-color`}
              leftUnit={
                <div
                  onClick={(e: React.MouseEvent<HTMLElement>): void => toggleColorPicker(e)}
                  style={{ width: 24, height: 24, backgroundColor: color, cursor: "pointer" }}
                ></div>
              }
              rightUnit={<KuikaSmallText>HEX</KuikaSmallText>}
            />
          </div>
        </div>
        {/* Portaled to <body>: the parent KuikaPopover positions itself with a
            CSS transform, which would make this `position: fixed` picker resolve
            against the popover box instead of the viewport. Portaling escapes
            that transformed containing block. The `.kuika_colorPicker` class
            keeps KuikaPopover's onInteractOutside guard from closing the popover. */}
        {createPortal(
          <div style={{ display: isColorPickerActive ? "block" : "none" }} className="kuika_colorPicker">
            <ColorPicker
              color={color}
              onChange={(next: string): void => onPatch({ color: next })}
              onCancel={(): void => setIsColorPickerActive(false)}
              isColorPickerActive={isColorPickerActive}
              hideHeaderTabs={true}
            />
          </div>,
          document.body
        )}
      </div>
    );
  }

  const config: SliderConfig = SLIDER_CONFIG_BY_TYPE[item.type];

  return (
    <div className="effects-filter__popover">
      {typeSelector}
      <div className="effects-filter__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>{config.label}</KuikaSmallText>
        <KuikaSlider
          value={item.value ?? config.defaultValue}
          onChange={(next: number): void => onPatch({ value: next })}
          min={config.min}
          max={config.max}
          step={config.step}
          precision={config.precision}
          unit={config.unit}
          size="sm"
          dataTestId={`${testIdBase}-value`}
        />
      </div>
    </div>
  );
};

export default FilterPopoverContent;
